import axios from 'axios';
import logger from '../utils/logger';

// Dynamic API Configuration (supports environment config, remote backend, local network IP, and mobile devices)
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return `${window.location.protocol}//${window.location.hostname}:3000`;
  }
  return 'http://localhost:3000';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor to add authentication token and adjust baseURL for mobile devices
apiClient.interceptors.request.use(
  (config) => {
    // Dynamically adjust baseURL if accessed via mobile phone on local network without explicit env setting
    if (!process.env.REACT_APP_API_URL && typeof window !== 'undefined' && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      if (config.baseURL && (config.baseURL.includes('localhost') || config.baseURL.includes('127.0.0.1'))) {
        config.baseURL = `${window.location.protocol}//${window.location.hostname}:3000`;
      }
    }

    // Get token from sessionStorage
    const token = sessionStorage.getItem('bciis_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common responses
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const responseErrorMessage = String(error.response?.data?.error || error.response?.data?.message || '').toLowerCase();

    const isPasswordEndpoint = /\/user\/\d+\/password|\/admins\/\d+\/password/.test(requestUrl);
    const isCurrentPasswordValidationError = responseErrorMessage.includes('current password is incorrect');

    // Handle common error responses
    if (status === 401 && !(isPasswordEndpoint && isCurrentPasswordValidationError)) {
      // Unauthorized - clear token and redirect to login
      sessionStorage.removeItem('bciis_auth_token');
      sessionStorage.removeItem('bciis_user');
      window.location.href = '/';
    }
    
    // Log richer error info for debugging (status, url, code, full axios JSON)
    try {
      const info = {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        url: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
        responseData: error.response?.data
      };
      // If axios supports toJSON(), include it (helps with ETIMEDOUT etc.)
      if (typeof error.toJSON === 'function') info.axios = error.toJSON();
      logger.error('API request failed', info);
    } catch (logErr) {
      logger.error('API request failed (log serialization error)', {
        originalMessage: error?.message,
        loggerMessage: logErr?.message
      });
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;